import React, { useEffect, useRef, useReducer } from 'react';
import { Input, FormControl } from '@airbus/components-react';
import { Paper, Popper } from '@mui/material';
import { getAllHtz } from '../../part_search/service/HtzService';
import { fieldValidator } from '../../../shared/utils/yupSchemaCreator';
import { TableRes } from '../../../shared/types';
import styles from './HtzEditCell.module.css';
import { produce } from 'immer';

interface FieldHtzProps {
  name: string;
  rowId: number | string;
  initialValues: Record<string, any>;
  blurHandler: (name: string, value: any) => void;
  disabled?: boolean;
  required?: boolean;
  field?: any;
  label?: string;
  language?: string;
  handleEnterTabBlur?: (e: React.KeyboardEvent) => void;
  tableRes?: TableRes;
}

const MIN_CHARS = 4;

// Simple FSM statuses for the field
// Suggest better approach than using const assertion while adhering to 'erasableSyntaxOnly' constraint
const Status = {
  Initial: 'initial',
  Typing: 'typing',
  AssistLoading: 'assistLoading',
  AssistReady: 'assistReady',
  Error: 'error',
} as const;

type Status = (typeof Status)[keyof typeof Status];

// Events
const Events = {
  SET_VALUE: 'SET_VALUE',
  SET_ASSIST: 'SET_ASSIST',
  SET_LOADING: 'SET_LOADING',
  TOUCH: 'TOUCH',
  SET_ERROR: 'SET_ERROR',
  OPEN_DROPDOWN: 'OPEN_DROPDOWN',
  CLOSE_DROPDOWN: 'CLOSE_DROPDOWN',
  RESET_ASSIST: 'RESET_ASSIST',
  COMMIT_VALUE: 'COMMIT_VALUE',
} as const;

type Events = (typeof Events)[keyof typeof Events];

type Action =
  | { type: typeof Events.SET_VALUE; value: string }
  | { type: typeof Events.SET_ASSIST; options: string[] }
  | { type: typeof Events.SET_LOADING; loading: boolean }
  | { type: typeof Events.TOUCH }
  | { type: typeof Events.SET_ERROR; error: any }
  | { type: typeof Events.OPEN_DROPDOWN }
  | { type: typeof Events.CLOSE_DROPDOWN }
  | { type: typeof Events.RESET_ASSIST }
  | { type: typeof Events.COMMIT_VALUE; error: any };

type State = {
  status: Status;
  value: string;
  assistOptions: string[];
  touched: boolean;
  error: any;
  dropdownOpen: boolean;
};

const initialState: State = {
  status: Status.Initial,
  value: '',
  assistOptions: [],
  touched: false,
  error: null,
  dropdownOpen: false,
};

function reducer(state: State, event: Action): State {
  return produce(state, (draft) => {
    switch (event.type) {
      case Events.SET_VALUE: {
        draft.value = event.value;
        draft.status = event.value?.trim()?.length >= MIN_CHARS ? Status.Typing : Status.Initial;
        return;
      }
      case Events.SET_LOADING: {
        draft.status = event.loading ? Status.AssistLoading : draft.status;
        return;
      }
      case Events.SET_ASSIST: {
        draft.assistOptions = event.options || [];
        draft.status = draft.assistOptions.length > 0 ? Status.AssistReady : Status.Typing;
        return;
      }
      case Events.RESET_ASSIST: {
        draft.assistOptions = [];
        draft.status = Status.Initial;
        return;
      }
      case Events.TOUCH: {
        draft.touched = true;
        return;
      }
      case Events.SET_ERROR: {
        draft.error = event.error;
        draft.status = event.error ? Status.Error : draft.status;
        return;
      }
      case Events.OPEN_DROPDOWN: {
        draft.dropdownOpen = true;
        return;
      }
      case Events.CLOSE_DROPDOWN: {
        draft.dropdownOpen = false;
        return;
      }
      case Events.COMMIT_VALUE: {
        draft.error = event.error;
        draft.touched = true;
        draft.dropdownOpen = false;
        if (event.error) {
          draft.status = Status.Error;
        }
        return;
      }
      default:
        return state;
    }
  });
}

function HtzFormField({
  name,
  rowId,
  initialValues,
  blurHandler,
  disabled,
  required,
  field,
  label,
  language,
  handleEnterTabBlur,
  tableRes,
}: FieldHtzProps) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const prevInitialValRef = useRef<string | null>(null);
  const screenGridId = tableRes?.id;
  const schemaName = tableRes?.mbdDataGrid?.schemaName;

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const debounceRef = useRef<any>(null);

  useEffect(() => {
    if (!initialValues) return;
    const initialVal = (initialValues[name] ?? '') as string;
    if (prevInitialValRef.current !== initialVal) {
      prevInitialValRef.current = initialVal;
      if (initialVal !== state.value) {
        dispatch({ type: Events.SET_VALUE, value: initialVal ?? '' });
      }
    }
    let isTouched = false;
    if (
      initialValues?.isCommitRow ||
      (initialValues?.status === 'E' && initialValues?.backup?.[name] !== initialValues[name]) ||
      ((initialValues?.status === 'N' || initialValues?.status === 'C') &&
        initialValues?.touched?.[name])
    ) {
      isTouched = true;
    }
    if (isTouched) {
      dispatch({ type: Events.TOUCH });
      const err = fieldValidator(field, initialVal, language);
      dispatch({ type: Events.SET_ERROR, error: err });
    }
  }, [initialValues, name, field, language]);

  useEffect(() => {
    const showAssist = state.value && state.value.trim().length >= MIN_CHARS;
    if (!showAssist) {
      dispatch({ type: Events.RESET_ASSIST });
      return;
    }
    const query = state.value.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      dispatch({ type: Events.SET_LOADING, loading: true });
      try {
        const data = await getAllHtz(query, screenGridId);
        const list = (data[`${schemaName}_qry_search`] ?? data ?? [])
          .map((item: any) => item?.htz)
          .filter(Boolean);
        dispatch({ type: Events.SET_ASSIST, options: list });
      } catch (e) {
        console.error(e);
        dispatch({ type: Events.SET_ASSIST, options: [] });
      } finally {
        dispatch({ type: Events.SET_LOADING, loading: false });
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [state.value, screenGridId, schemaName]);

  useEffect(() => {
    if (disabled || (state.value?.trim().length ?? 0) < MIN_CHARS) {
      dispatch({ type: Events.CLOSE_DROPDOWN });
    }
  }, [disabled, state.value]);

  useEffect(() => {
    const handleDocMouseDown = (e: MouseEvent) => {
      if (!anchorRef.current) return;
      if (anchorRef.current.contains(e.target as Node)) return;
      dispatch({ type: Events.CLOSE_DROPDOWN });
    };
    document.addEventListener('mousedown', handleDocMouseDown);
    return () => document.removeEventListener('mousedown', handleDocMouseDown);
  }, []);

  const commitValue = (finalVal: string) => {
    if (disabled) return;
    const err = fieldValidator(field, finalVal, language);
    dispatch({ type: Events.COMMIT_VALUE, error: err });

    if (initialValues[name] === finalVal) return;
    blurHandler(name, err?.type === 'invalid' ? null : finalVal || null);
    if (field?.onValueChange) {
      field.onValueChange({ rowId, value: finalVal, row: initialValues });
    }
  };

  const handleChange = (v: string) => {
    dispatch({ type: Events.SET_VALUE, value: v });
    if (!state.touched) {
      const err = fieldValidator(field, v, language);
      dispatch({ type: Events.SET_ERROR, error: err });
    }
    if (!disabled && (v?.trim().length ?? 0) >= MIN_CHARS) {
      dispatch({ type: Events.OPEN_DROPDOWN });
    }
  };

  const handleSuggestionClick = (selected: string) => {
    dispatch({ type: Events.SET_VALUE, value: selected });
    dispatch({ type: Events.SET_ASSIST, options: [] });
    commitValue(selected);
  };

  const handleFocus = () => {
    dispatch({ type: Events.TOUCH });
    if (!disabled && (state.value?.trim().length ?? 0) >= MIN_CHARS) {
      dispatch({ type: Events.OPEN_DROPDOWN });
    }
  };

  const handleInputBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
    const next = e.relatedTarget as Node | null;
    if (anchorRef.current && next && anchorRef.current.contains(next)) {
      return;
    }
    commitValue(state.value);
  };

  const isAssistLoading = state.status === Status.AssistLoading;
  const open = state.dropdownOpen && (state.assistOptions.length > 0 || isAssistLoading);

  return (
    <FormControl
      id={name}
      error={state.touched && Boolean(state.error)}
      {...{ label, name, required, disabled }}
      helpText={state.touched && state.error?.msg}
      errorText={state.touched && state.error?.msg}
      onKeyDown={handleEnterTabBlur}
      className='disabled-input'
    >
      <div {...{ name }} ref={anchorRef} className={styles.container}>
        <Input
          type='text'
          {...{ name }}
          size='small'
          value={state.value ?? ''}
          disabled={disabled}
          onFocus={handleFocus}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleInputBlur}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Tab') {
              (e.target as HTMLInputElement).blur();
            }
            handleEnterTabBlur?.(e);
          }}
          className={styles.input}
          id={`${rowId}_${name}`}
          autoComplete='off'
        />
        <Popper
          open={!disabled && (state.value?.length ?? 0) >= MIN_CHARS && open}
          anchorEl={anchorRef.current}
          placement='bottom-start'
          style={{ zIndex: 1301, width: anchorRef.current?.clientWidth }}
          modifiers={[{ name: 'preventOverflow', options: { boundary: 'window' } }]}
        >
          <Paper elevation={3} className={styles.popperPaper}>
            <ul className={styles.suggestionList}>
              {!isAssistLoading &&
                state.assistOptions.map((htzString, i) => (
                  <li
                    key={i}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSuggestionClick(htzString);
                    }}
                    className={styles.suggestionItem}
                    tabIndex={-1}
                    aria-selected={state.value === htzString}
                  >
                    {htzString}
                  </li>
                ))}
            </ul>
          </Paper>
        </Popper>
      </div>
    </FormControl>
  );
}

export default HtzFormField;
